import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, Separator, VertBox } from 'component/basics';
import { FilterButton } from 'component/button';
import { InformationalModal } from 'component/modal/informational-modal';
import { TextField, UtilityText } from 'component/text';
import { useState } from 'react';
import { Pressable } from 'react-native';
import type { ExternalEvalSpace } from 'structure/ranking-eval';
import { t } from 'util/stdtypes';

const usePickerStyles = createThemedStyles((tokens) => ({
    row: { paddingVertical: 10, paddingHorizontal: 8, borderRadius: 4 },
    currentRow: { backgroundColor: tokens.colors.colorSurfaceSecondary },
}));

function ConversationPickerList({
    filteredKeys,
    allConversations,
    instanceKey,
    onNavigate,
    onClose,
}: {
    filteredKeys: string[];
    allConversations: Record<string, ExternalEvalSpace>;
    instanceKey: string;
    onNavigate: (key: string) => void;
    onClose: () => void;
}) {
    const styles = usePickerStyles();
    const { colors } = useTheme();
    return (
        <VertBox gap={0}>
            {filteredKeys.map((key) => {
                const conv = allConversations[key];
                const name = conv?.name || key;
                const isCurrent = key === instanceKey;
                return (
                    <Pressable
                        key={key}
                        onPress={() => {
                            onNavigate(key);
                            onClose();
                        }}
                        style={[styles.row, isCurrent && styles.currentRow]}
                    >
                        <UtilityText text={name} type='small' weight={isCurrent ? 'medium' : 'regular'} />
                        {conv?.commentCount != null && (
                            <UtilityText
                                color={colors.colorTextSecondary}
                                formatParams={{ count: conv.commentCount }}
                                label={t('ranking-eval.comment_count', '{count} comments')}
                                type='tiny'
                            />
                        )}
                    </Pressable>
                );
            })}
            {filteredKeys.length === 0 && (
                <UtilityText
                    color={colors.colorTextSecondary}
                    label={t('ranking-eval.no_conversations_match', 'No conversations match.')}
                    type='small'
                />
            )}
        </VertBox>
    );
}

export function ConversationPickerModal({
    allConversations,
    selectedKeys,
    instanceKey,
    onNavigate,
    onClose,
}: {
    allConversations: Record<string, ExternalEvalSpace>;
    selectedKeys: string[];
    instanceKey: string;
    onNavigate: (key: string) => void;
    onClose: () => void;
}) {
    const hasSelected = selectedKeys.length > 0;
    const [showSelectedOnly, setShowSelectedOnly] = useState(hasSelected);
    const [search, setSearch] = useState('');
    const { colors } = useTheme();

    const allKeys = Object.keys(allConversations);
    const displayKeys = showSelectedOnly ? selectedKeys : allKeys;
    const filteredKeys = search
        ? displayKeys.filter((k) => {
              const name = allConversations[k]?.name || k;
              return name.toLowerCase().includes(search.toLowerCase());
          })
        : displayKeys;

    return (
        <InformationalModal
            content={
                <VertBox gap={16} wide>
                    <TextField
                        onChange={setSearch}
                        placeholderLabel={t('ranking-eval.search_conversations', 'Search conversations...')}
                        value={search}
                    />
                    <HorizBox center gap={8}>
                        <FilterButton
                            label={t('ranking-eval.filter_selected', 'Selected')}
                            onPress={() => setShowSelectedOnly(true)}
                            selected={showSelectedOnly}
                        />
                        <FilterButton
                            label={t('ranking-eval.filter_all', 'All')}
                            onPress={() => setShowSelectedOnly(false)}
                            selected={!showSelectedOnly}
                        />
                        <UtilityText
                            color={colors.colorTextSecondary}
                            formatParams={{ count: filteredKeys.length }}
                            label={t('ranking-eval.conversation_count', '{count} conversations')}
                            type='small'
                        />
                    </HorizBox>
                    <Separator />
                    <ConversationPickerList
                        allConversations={allConversations}
                        filteredKeys={filteredKeys}
                        instanceKey={instanceKey}
                        onClose={onClose}
                        onNavigate={onNavigate}
                    />
                </VertBox>
            }
            headingLabel={t('ranking-eval.select_conversation', 'Select Conversation')}
            onClose={onClose}
        />
    );
}
